<template>
    <v-form @submit.prevent="saveExchangeRate" ref="form">
        <v-card class="elevation-1 rounded-4"> 
            <v-toolbar flat height="45px">
                <span class="font-weight-bold primary--text">SET EXCHANGE RATE</span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog" :disabled="disabled">
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
           <v-card-text>
                
                <v-alert v-if="error" :value="true" color="error" icon="error" outline class="rounded-4">
                    Error encountered. Please contact support. Error details: {{errorMessage}}
                </v-alert> 

                <!-- Cutoff Period, Base and Target Currencies -->
                <v-layout row wrap>
                    <v-flex xs12 sm6 class="form-item">
                        <div class="caption grey--text text--darken-1">Cutoff Period</div>
                        <div class="text--darken-2 font-weight-bold">
                            {{ cutoffStart | moment('MMM DD') }} to {{ cutoffEnd | moment('DD, YYYY') }}
                        </div>
                    </v-flex>

                    <v-flex xs6 sm3 :class="isMobile?'form-item mt-10':'form-item'">
                        <div class="caption grey--text text--darken-1">Base Currency</div>
                        <div class="font-weight-bold">
                            <v-chip class="chip--select-multi">
                                <v-avatar size="24">
                                    <v-img :src="getCurrencyAvatar(defaultCurrency)" contain></v-img>
                                </v-avatar>
                                {{ defaultCurrency }}
                            </v-chip>
                        </div>
                    </v-flex>

                    <v-flex xs6 sm3 :class="isMobile?'form-item mt-10':'form-item'">
                        <div class="caption grey--text text--darken-1">Target Currency</div>
                        <div class="font-weight-bold">
                            <v-chip class="chip--select-multi">
                                <v-avatar size="24">
                                    <v-img :src="ph_currency.avatar" contain></v-img>
                                </v-avatar>
                                <span>PHP</span>
                            </v-chip>
                        </div>
                    </v-flex>
                </v-layout>

                <!-- Exchange Rate -->
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-item ">
                        <v-text-field 
                            label="Enter Exchange Rate"
                            v-model="exchangeRate"
                            :rules="[rules.required,rules.max_amount,rules.currency]"
                            type="number" min="1" step="1" value="0.000000" 
                            validate-on-blur
                            :disabled="disabled"
                            @keyup="error=false"></v-text-field>
                    </v-flex>
                </v-layout>
                
                <!-- Rate Insights -->
                <v-layout v-if="insights" row wrap class="px-3 mt-4">
                    <v-flex xs12>
                        <v-card flat class="grey lighten-2 rounded-lg pa-3">
                            <!-- Title Block with zeroed bottom margins -->
                            <div class="font-weight-bold primary--text ml-15">
                                RATE INSIGHTS
                            </div>
                            
                            <!-- Dense Content List -->
                            <v-list dense class="transparent pa-0">
                                
                                <v-list-tile class="px-0 height-auto">
                                    <v-list-tile-content class="body-2 grey--text text--darken-2 py-1">
                                        {{ insights.latestRateLabel || 'Latest Market Reference' }}
                                    </v-list-tile-content>
                                    <v-list-tile-action class="my-0">
                                        <v-chip small class="font-weight-bold ma-0" color="blue lighten-5" text-color="blue darken-4"  @click="applyInsightRate(insights.yesterdayRate)">
                                            {{ numFormat.formatNumber(insights.yesterdayRate, 6) }}
                                        </v-chip>
                                    </v-list-tile-action>
                                </v-list-tile>

                                <v-list-tile class="px-0 height-auto">
                                    <v-list-tile-content class="body-2 grey--text text--darken-2 py-1">
                                        {{ insights.currentMonthLabel || 'Current Month' }} Average
                                    </v-list-tile-content>
                                    <v-list-tile-action class="my-0">
                                        <v-chip small class="font-weight-bold ma-0" color="blue lighten-5" text-color="blue darken-4" @click="applyInsightRate(insights.currentMonthAverage)">
                                            {{ numFormat.formatNumber(insights.currentMonthAverage, 6) }}
                                        </v-chip>
                                    </v-list-tile-action>
                                </v-list-tile>

                                <v-list-tile class="px-0 height-auto">
                                    <v-list-tile-content class="body-2 grey--text text--darken-2 py-1">
                                        Historical Average ({{ insights.previous3MonthLabel || 'Prev 3 Months' }})
                                    </v-list-tile-content>
                                    <v-list-tile-action class="my-0">
                                        <v-chip small class="font-weight-bold ma-0" color="teal lighten-5" text-color="teal darken-4" @click="applyInsightRate(insights.previous3MonthAverage)">
                                            {{ numFormat.formatNumber(insights.previous3MonthAverage, 6) }}
                                        </v-chip>
                                    </v-list-tile-action>
                                </v-list-tile>

                            </v-list>

                             <!-- 🔒 Data Source Explanatory Footer Note -->
                            <v-divider class="my-2"></v-divider>
                            <div class="caption grey--text text--darken-2 font-italic mr-10 ml-15">
                                Rates are blended mid-market baseline aggregated from multiple commercial feeds and central banks.
                            </div>
                        </v-card>
                    </v-flex>
                </v-layout>

            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mr-25">
                <v-spacer></v-spacer>
                <v-btn type="submit" class="primary" dark :loading="loading">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import clientApi from '@/api/client'
import forexApi from '@/api/forex'
import numFormat from '@/common/num_format'     //for refactor expose only function
import { ph_currency, getCurrencyAvatar } from '@/constants/default-currencies'

export default {
    data () {
        return {
            ph_currency,
            numFormat,
            error: false,
            errorMessage: '',
            loading: false,
            disabled: false,

            //for refactor - put in rules-helper.js
            rules: {
                required: value => !!value || 'Required.',
                max_amount: value => value<=10000 || 'Invalid Amount. Maximum of 10,000.00',
                currency: value => {
                    if (!value) return true; // allow empty if optional
                    // Must be positive, up to 6 decimal places
                    const pattern = /^\d+(\.\d{1,6})?$/;
                    return pattern.test(value) || 'Invalid currency format. Up to 6 decimals allowed.';
                }
            },
            
            insights: null,

            defaultCurrency: null,
            clientForexSettingId: null,
            clientId: null,
            cutoffId: null,
            cutoffStart: null,
            cutoffEnd: null,
            exchangeRate: null
        }
    },
    methods: {
        getCurrencyAvatar,
        applyInsightRate(rate) {
            this.exchangeRate = numFormat.formatNumber(rate,6)
        },
        async getAverage() {
            try {
                const response = await forexApi.getAverage(this.defaultCurrency)
                if (response && response.status == 200) {
                    this.insights = response.data
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async saveExchangeRate() {
            try {
                if (this.$refs.form.validate()) { 
                    this.loading = true
                    this.disabled = true

                    const data = {
                        clientForexSettingId: this.clientForexSettingId,
                        clientId: this.clientId,
                        baseCurrency: this.defaultCurrency,
                        targetCurrency: 'PHP',
                        cutoffId: this.cutoffId,
                        exchangeRate: this.exchangeRate
                    }
                    const response = await clientApi.updateForexSetting(data)

                    if (response && response.status == 200) {
                        this.$emit('send', null)
                        this.loading = false
                        this.disabled = false
                        this.closeDialog()
                    }
                }
            } catch(error) {
                this.errorMessage = error
                this.error = true
            }
        },
        async setEdit(data, client) {
            this.defaultCurrency = client.defaultCurrency
            this.clientId = client.clientId

            this.clientForexSettingId = data.clientForexSettingId            
            this.cutoffId = data.cutoffId
            this.cutoffStart = data.cutoffStart
            this.cutoffEnd = data.cutoffEnd

            if (data.exchangeRate > 0)
                this.exchangeRate = Number(data.exchangeRate).toFixed(6)
            else 
                this.exchangeRate = null

            if (!this.insights) {
                await this.getAverage()
            }
        },
        closeDialog() {
            this.$emit('update:dialogEditExchangeRate', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.loading = false
            this.error = false
            this.errorMessage = ''
        },
    },
}
</script>
<style scoped>
.form-item {
    padding: 0px 20px 0px 20px;
}
</style>